import React, { useEffect, useRef, useState } from "react";
import DropDownIcon from "../../assets/drop-down.svg";
import { Tick } from "../AdminIcons";

const FormSelect = ({
  label,
  isRequired = false,
  id,
  options = [],
  defaultValue = "",
  value,
  placeholder,
  emptyByDefault = true,
  className = "",
  labelClassName = "",
  controlWrapperClassName = "",
  selectClassName = "",
  dropdownClassName = "",
  dropdownPlacement = "bottom",
  optionClassName = "",
  icon: IconComponent,
  iconProps = {},
  optionIcon: OptionIconComponent,
  optionIconProps = {},
  placeholderClassName = "",
  keepPlaceholderOnSelect = false,
  onSubOptionsChange,
  initialSelectedSubOptions,
  onOpen,
  showTotalSelectedCount = false,
  showPerOptionSelectedCount = true,
  onChange,
  ...props
}) => {
  const rootRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value ?? defaultValue);
  const [expandedOption, setExpandedOption] = useState(value ?? defaultValue);
  const [selectedSubOptions, setSelectedSubOptions] = useState(
    () => initialSelectedSubOptions ?? {},
  );

  useEffect(() => {
    if (initialSelectedSubOptions !== undefined) {
      setSelectedSubOptions(initialSelectedSubOptions);
    }
  }, [initialSelectedSubOptions]);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, []);

  useEffect(() => {
    // prefer controlled `value` prop if provided, otherwise fall back to `defaultValue`
    const next = value !== undefined ? value : defaultValue;
    setSelectedValue(next);
    setExpandedOption(next);
  }, [defaultValue, value]);

  const getSelectedCount = (optionValue) =>
    (selectedSubOptions[optionValue] || []).length;

  const getTotalSelectedCount = () =>
    Object.values(selectedSubOptions).reduce(
      (total, selectedItems) => total + selectedItems.length,
      0,
    );

  const getDisplayLabelWithCount = (option) => {
    if (!option) {
      return "";
    }

    const selectedCount = getSelectedCount(option.value);
    if (showPerOptionSelectedCount && selectedCount > 0) {
      return `${option.label} {${selectedCount} selected}`;
    }

    return option.label;
  };

  const selectedOption = options.find((option) => option.value === selectedValue);
  const selectedOptionLabel = getDisplayLabelWithCount(selectedOption);
  const totalSelectedCount = getTotalSelectedCount();
  const hasSelection =
    (showTotalSelectedCount && totalSelectedCount > 0) ||
    Boolean(selectedOptionLabel) ||
    (!emptyByDefault && options.length > 0);

  let displayText;
  if (keepPlaceholderOnSelect) {
    displayText = placeholder || (emptyByDefault ? "" : options[0]?.label || "");
  } else {
    displayText =
      (showTotalSelectedCount && totalSelectedCount > 0
        ? `${totalSelectedCount} selected`
        : selectedOptionLabel) ||
      (emptyByDefault ? placeholder || "" : options[0]?.label || "");
  }

  const handleSelect = (option) => {
    const optionValue = option.value;
    setSelectedValue(optionValue);

    if (option.subOptions?.length) {
      setExpandedOption((current) => (current === optionValue ? "" : optionValue));
    } else {
      setIsOpen(false);
      setExpandedOption(optionValue);
    }

    if (typeof onChange === "function") {
      onChange(optionValue);
    }
  };

  const toggleSubOption = (optionValue, subOptionValue) => {
    setSelectedSubOptions((current) => {
      const currentSelection = current[optionValue] || [];
      const nextSelection = currentSelection.includes(subOptionValue)
        ? currentSelection.filter((value) => value !== subOptionValue)
        : [...currentSelection, subOptionValue];

      const nextState = {
        ...current,
        [optionValue]: nextSelection,
      };

      if (typeof onSubOptionsChange === "function") {
        onSubOptionsChange(nextState);
      }

      return nextState;
    });
  };

  return (
    <div ref={rootRef} className={className}>
      {label ? (
        <label className={`block leading-5 text-black ${labelClassName}`} htmlFor={id}>
          {label} {isRequired && <span className="text-[#EF4444]">*</span>}
        </label>
      ) : null}

      <div className={`relative ${controlWrapperClassName}`}>
        <button
          id={id}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={() => {
            setIsOpen((current) => {
              const next = !current;

              if (next && typeof onOpen === "function") {
                onOpen();
              }

              return next;
            });
          }}
          className={`flex  w-full items-center justify-between gap-3 rounded-lg border border-[#938A96] bg-transparent text-left text-black outline-none ${selectClassName}`}
          {...props}
        >
          <span
            className={
              displayText
                ? `block flex-1 truncate ${keepPlaceholderOnSelect ? (placeholderClassName || "text-[#6B7280]") : (hasSelection ? "text-black" : (placeholderClassName || "text-[#6B7280]"))}`
                : "block flex-1 text-transparent"
            }
          >
            {displayText || "_"}
          </span>
          <span className="pointer-events-none flex items-center justify-center">
            {IconComponent ? (
              <IconComponent {...iconProps} />
            ) : (
              <img
                src={DropDownIcon}
                alt=""
                aria-hidden="true"
                className="block h-2 w-2"
              />
            )}
          </span>
        </button>

        {isOpen ? (
          <div
            role="listbox"
            className={`absolute right-0 z-50 max-h-60 overflow-auto border border-[#0000004D] bg-[#E8E8E8] ${
              dropdownPlacement === "top"
                ? "bottom-full mb-2"
                : "top-full mt-2"
            } ${dropdownClassName}`}
          >
            {options.map((option, index) => {
              const isSelected = option.value === selectedValue;
              const isExpanded = expandedOption === option.value;
              const hasSubOptions = Array.isArray(option.subOptions) && option.subOptions.length > 0;

              return (
                <div key={`${option.value}-${index}`}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(option)}
                    className={`block w-full px-2 py-2 text-left text-black transition-colors duration-150 hover:bg-[#F5F5F5] ${optionClassName} 
                    `}
                  >
                    <span className="flex items-center justify-between gap-3">
                      <span>{getDisplayLabelWithCount(option)}</span>
                      {OptionIconComponent ? (
                        <span
                          className={`inline-flex items-center justify-center transition-transform duration-150 ${
                            hasSubOptions && isExpanded ? "rotate-180" : ""
                          }`}
                        >
                          <OptionIconComponent {...optionIconProps} />
                        </span>
                      ) : null}
                    </span>
                  </button>

                  {hasSubOptions && isExpanded ? (
                    <div className="space-y-1 pb-2 pl-8 pr-3">
                      {option.subOptions.map((subOption) => {
                        const checked = (selectedSubOptions[option.value] || []).includes(
                          subOption.value,
                        );

                        return (
                          <button
                            key={`${option.value}-${subOption.value}`}
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleSubOption(option.value, subOption.value);
                            }}
                            className="flex w-full text-sm items-center gap-2.5  px-2 py-1 text-left text-[#000]"
                          >
                            <span
                              className={`inline-flex h-4 w-4 items-center justify-center  border-[1.5px] rounded-sm  ${
                                checked
                                  ? "border-[#923D8C] bg-[#923D8C] text-white"
                                  : "border-[#923D8C] text-[#923D8C]"
                              }`}
                            >
                              <Tick/>
                            </span>
                            <span>{subOption.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default FormSelect;